import React from 'react';
import { createRoot } from 'react-dom/client';
import { jwtDecode } from 'jwt-decode';
import { Provider, useSelector } from 'react-redux';
import { MsalProvider } from '@azure/msal-react';
import { PersistGate } from 'redux-persist/integration/react';
import { BrowserRouter } from 'react-router-dom';
import moment from 'moment';

import { msalInstance, initializeMsal } from 'Configuration/MsalConfiguration';
import { getMsalAccount, getMsalIdToken } from 'Services/MsalTokenService';
import App from 'App';
import Unauthorized from 'Views/Unauthorized';
import Maintenance from 'Views/Maintenance';
import { store, persistor } from 'Configuration/StoreConfiguration';
import { VALID_LOGIN_F2W_ROLES } from 'Constants/F2wRolesConstants';
import { getF2wSettings } from 'Store/Actions/Setting/SettingAction';
import 'Configuration/AgGridConfiguration';
import F2WCustomLogger from 'Services/F2WCustomLogger';

const ADMIN_EMAILS = [
  'paulina.przytocka@tui.com',
  'joseenrique.mateu@tui.com',
  'rebecca.powell@tui.co.uk',
  'Rebecca.Powell@tui.co.uk',
];

const isInMaintenanceWindow = (f2wSettings) => {
  if (!f2wSettings?.startDate || !f2wSettings?.endDate) {
    return false;
  }
  const now = moment.utc();
  const start = moment.utc(f2wSettings.startDate);
  const end = moment.utc(f2wSettings.endDate);
  return now.isBetween(start, end, null, '[]');
};

const MaintenanceWrapper = ({ isAdmin, profile }) => {
  const f2wSettings = useSelector((state) => state.settings?.f2wSettings);

  if (!f2wSettings) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="text-center">
          <i className="fa fa-spinner fa-spin fa-2x me-2" />
          <span className="fs-5">Loading Fly2Work Web...</span>
        </div>
      </div>
    );
  }

  const maintenanceActive = isInMaintenanceWindow(f2wSettings);

  if (maintenanceActive && !isAdmin) {
    return (
      <MsalProvider instance={msalInstance}>
        <Maintenance startDate={f2wSettings.startDate} endDate={f2wSettings.endDate} />
      </MsalProvider>
    );
  }

  const roles = profile.roles || [];
  const validRoles = roles.filter((role) => VALID_LOGIN_F2W_ROLES.includes(role));

  if (validRoles.length === 0) {
    throw new Error('401');
  }

  return (
    <MsalProvider instance={msalInstance}>
      <App />
    </MsalProvider>
  );
};

const root = createRoot(document.getElementById('root'));

const renderApp = (Component) => {
  root.render(
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>{Component}</BrowserRouter>
      </PersistGate>
    </Provider>
  );
};

const initializeApp = async () => {
  try {
    await initializeMsal();
    await msalInstance.handleRedirectPromise();

    const account = getMsalAccount();

    if (!account) {
      await msalInstance.loginRedirect();
      return;
    }

    const token = await getMsalIdToken();
    const profile = jwtDecode(token);

    const userEmail = (profile.upn || profile.email || '').toLowerCase();
    const isAdmin = ADMIN_EMAILS.map((e) => e.toLowerCase()).includes(userEmail);

    await store.dispatch(getF2wSettings());

    renderApp(<MaintenanceWrapper isAdmin={isAdmin} profile={profile} />);
  } catch (error) {
    F2WCustomLogger.error('Startup Error', error, { component: 'index' });

    if (error.errorCode === 'interaction_in_progress') {
      return;
    }

    if (error.name === 'InteractionRequiredAuthError') {
      await msalInstance.loginRedirect();
    } else {
      renderApp(
        <MsalProvider instance={msalInstance}>
          <Unauthorized errorMessage={error.message} />
        </MsalProvider>
      );
    }
  }
};

initializeApp();
